import useResearchModels from '../../../hooks/useResearchModels';
import TranslationModelSelect from './TranslationModelSelect';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox,
  Chip, FormControlLabel, LinearProgress, Link, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { axiosInstance } from '../../../config';
import { requestErrorDetails, requestErrorMessage } from '../../../hooks/requestError.mjs';

const names = {
  originator: 'Originator', companyName: 'Company', category: 'Subcategory',
  variants: 'Medicine variations', availability: 'Availability', metaKeyword: 'Meta keywords',
  metaTitle: 'Meta title', pageTitle: 'Page title', metaDesc: 'Meta description',
};
const flatten = draft => [
  ...Object.entries(draft?.fields || {})
    .filter(([key, value]) => key !== 'additionalSections' && key !== 'country'
      && value !== undefined && value !== null
      && (Array.isArray(value) ? value.length : typeof value !== 'string' || value.trim()))
    .map(([key, value]) => ({ key, label: names[key] || key, value })),
  ...(draft?.fields.additionalSections || []).filter(section => section.content?.trim())
    .map(section => ({ key: 'section:' + section.sectionName, label: section.sectionName, value: section.content })),
];
const text = value => {
  if (Array.isArray(value)) return value.map(variant => [
    variant.dosageForm || 'Dosage form not supplied', variant.strength || 'Strength not supplied',
    variant.packTypes || '', variant.minimumOrderQty ? `Minimum order: ${variant.minimumOrderQty}` : '',
    variant.sellingPrice ? `Price: ${variant.sellingPrice}` : '', variant.weight ? `Weight: ${variant.weight}` : '',
  ].filter(Boolean).join(' · ')).join('\n');
  if (typeof value !== 'string') return String(value);
  return new DOMParser().parseFromString(value, 'text/html').body.textContent || '';
};
const same = (a, b) => String(a || '').normalize('NFKC').trim().toLowerCase()
  === String(b || '').normalize('NFKC').trim().toLowerCase();
const emptyResult = { applied: [], needsManual: [] };

export default function MedicineResearchPanel({
  generic, brand, setGeneric, setBrand, onApply, onStart, sourceId,
  disabled = false, hasVariants = false, readyToApply = true,
}) {
  const models = useResearchModels();
  const location = useLocation(), navigate = useNavigate();
  const id = new URLSearchParams(location.search).get('research');
  const [additional, setAdditional] = useState('');
  const [run, setRun] = useState(null), [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [needsStatusCheck, setNeedsStatusCheck] = useState(false), [checkingStatus, setCheckingStatus] = useState(false);
  const [refreshingModels, setRefreshingModels] = useState(false);
  const [selected, setSelected] = useState([]), [onlyEmpty, setOnlyEmpty] = useState(true);
  const [message, setMessage] = useState(null), [detailsOpen, setDetailsOpen] = useState(false);
  const mounted = useRef(true), pending = useRef(false);
  const nonce = useRef({ key: '', id: '' }), hydrated = useRef(null);
  const unconfirmed = useRef('');
  const selectionSeed = useRef(null), linked = useRef(null), autoApplied = useRef(new Set());
  const inputRef = useRef({ generic, brand }); inputRef.current = { generic, brand };
  const held = run?.status === 'held';
  const active = run && ['queued', 'claimed', 'sent', 'received', 'held'].includes(run.status);
  const matches = !run || (same(generic, run.input.generic) && same(brand, run.input.brand));
  const items = flatten(run?.draft);
  const adminBase = location.pathname.split('/medicines')[0];
  const selectedModel = models.catalogue?.models.find(model => model.id === models.selection);
  const modeDescription = models.billingMode === 'free'
    ? 'Uses a free route only. If free access or quota is unavailable, generation stops without a paid request.'
    : models.billingMode === 'paid'
      ? 'Uses the selected model’s configured paid route. Paid usage uses the dashboard budget.'
      : 'Tries free for the selected model when available; paid usage uses the dashboard budget. The selected model stays the same.';

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const setId = value => {
    const next = new URLSearchParams(location.search); next.set('research', value);
    navigate(`${location.pathname}?${next}`, { replace: true });
  };
  useEffect(() => {
    let live = true;
    axiosInstance.get('/api/translations/research/runs', { timeout: 15000 }).then(response => {
      if (live) setHistory(response.data);
    }).catch(() => {});
    return () => { live = false; };
  }, [id, run?.status]);
  useEffect(() => {
    if (!id) { setRun(null); return; }
    let live = true, loading = false, timer;
    async function load() {
      if (loading) return; loading = true;
      try {
        const response = await axiosInstance.get(`/api/translations/research/runs/${id}`, { timeout: 15000 });
        if (!live) return;
        setRun(response.data); setError('');
        if (['completed', 'failed', 'uncertain'].includes(response.data.status)) clearInterval(timer);
      } catch (failure) {
        if (live) setError(requestErrorDetails(failure, { readOnly: true, fallback: 'Generation progress could not be loaded.' }).message);
      } finally { loading = false; }
    }
    load(); timer = setInterval(load, 4000);
    return () => { live = false; clearInterval(timer); };
  }, [id]);
  useEffect(() => {
    if (!run || hydrated.current === run.id) return;
    hydrated.current = run.id;
    const blank = !inputRef.current.generic && !inputRef.current.brand;
    const matching = same(inputRef.current.generic, run.input.generic) && same(inputRef.current.brand, run.input.brand);
    setAdditional(blank || matching ? run.input.additional || '' : '');
    if (blank) { onStart?.(); setGeneric(run.input.generic); setBrand(run.input.brand); }
  }, [run, setGeneric, setBrand, onStart]);
  useEffect(() => {
    if (!run?.draft) return;
    if (selectionSeed.current !== run.id) {
      selectionSeed.current = run.id;
      setSelected(flatten(run.draft).filter(row => row.key !== 'variants' || !hasVariants).map(row => row.key));
    } else if (hasVariants) {
      setSelected(previous => previous.includes('variants') ? previous.filter(key => key !== 'variants') : previous);
    }
  }, [run?.id, run?.draft, hasVariants]);
  useEffect(() => {
    if (!sourceId || !run || run.sourceId || !matches || linked.current === run.id) return;
    linked.current = run.id;
    axiosInstance.post(`/api/translations/research/runs/${run.id}/attach`, { sourceId: String(sourceId) })
      .then(response => { if (mounted.current) setRun(response.data); }).catch(() => {});
  }, [sourceId, run, matches]);
  useEffect(() => {
    if (!run?.draft || run.status !== 'completed' || run.id !== id || !matches
      || disabled || !readyToApply || autoApplied.current.has(run.id)
      || (run.sourceId && String(run.sourceId) !== String(sourceId || ''))) return;
    // Fill once while mounted. The form tracks prior AI values: refresh only
    // unchanged generated content and preserve edits made by the user.
    autoApplied.current.add(run.id);
    const keys = flatten(run.draft).map(row => row.key);
    try { setMessage(onApply({ draft: run.draft, selected: keys, onlyEmpty: true, automatic: true }) || emptyResult); }
    catch { setError('The generated draft is saved. Open details to apply its fields again.'); }
  }, [run, id, matches, disabled, readyToApply, hasVariants, onApply, sourceId]);

  async function start(retry = false) {
    if (pending.current || !models.ready || run?.status === 'uncertain' || needsStatusCheck) return;
    // Reuse the same UUID after a lost accepted response; never create another
    // generation just because the browser did not receive the first response.
    const key = JSON.stringify([generic.trim(), brand.trim(), additional.trim(), sourceId || null,
      models.selection, models.billingMode, retry ? run?.id : null]);
    if (unconfirmed.current && unconfirmed.current !== key) { setError('The earlier submission is unconfirmed. Restore its original model and input to retry the same request, or check its saved progress first.'); return; }
    pending.current = true; setBusy(true); setError(''); setMessage(null); onStart?.();
    if (nonce.current.key !== key) nonce.current = { key, id: crypto.randomUUID() };
    try {
      const response = await axiosInstance.post('/api/translations/research/runs', {
        requestId: nonce.current.id, generic, brand, additional,
        sourceId: sourceId || null, selection: models.selection, billingMode: models.billingMode,
      }, { timeout: 30000 });
      if (mounted.current) { unconfirmed.current = ''; setNeedsStatusCheck(false); setRun(response.data); setId(response.data.id); }
    } catch (failure) {
      if (mounted.current) setError(requestErrorMessage(failure, 'Generation could not be confirmed. Check its saved progress before submitting again.'));
      if (mounted.current && (!failure.response || requestErrorDetails(failure).uncertain)) { unconfirmed.current = key; setNeedsStatusCheck(true); }
    } finally { pending.current = false; if (mounted.current) setBusy(false); }
  }
  async function checkSavedGeneration() {
    if (!nonce.current.id || checkingStatus) return;
    setCheckingStatus(true);
    try {
      const { data } = await axiosInstance.get(`/api/translations/research/runs/${nonce.current.id}`, { timeout: 15000 });
      if (data?.id !== nonce.current.id || typeof data?.status !== 'string') throw new Error('The saved generation receipt was not confirmed.');
      if (mounted.current) { unconfirmed.current = ''; setNeedsStatusCheck(false); setError(''); setRun(data); setId(data.id); }
    } catch (failure) {
      if (mounted.current) {
        if (failure.response?.status === 404) { setNeedsStatusCheck(false); setError('No saved receipt was found yet. Keep the same model and input to retry the original request, or check saved progress again.'); }
        else setError(requestErrorDetails(failure, { readOnly: true, fallback: 'Saved generation progress could not be loaded.' }).message);
      }
    } finally { if (mounted.current) setCheckingStatus(false); }
  }
  function apply() {
    if (!run?.draft || !matches) return;
    setMessage(onApply({ draft: run.draft, selected, onlyEmpty }) || emptyResult);
  }

  return <Box component="section" aria-label="AI medicine research" sx={{ p: { xs: 2, sm: 3 }, mb: 3,
    bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, minWidth: 0 }}>
    <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
      <AutoAwesomeIcon color="primary" /><Typography variant="h6">Create medicine with AI</Typography>
    </Stack>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Enter the generic and brand, then add any product details in your own words. AI fills the form using your medicine template. You can edit everything before saving.
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2,minmax(0,1fr))' }, gap: 2 }}>
      <TextField label="A. Generic name" value={generic} onChange={event => { onStart?.(); setGeneric(event.target.value); }}
        disabled={disabled || busy || active || needsStatusCheck} inputProps={{ maxLength: 300 }} helperText="Example: Binimetinib" />
      <TextField label="B. Generic brand name" value={brand} onChange={event => { onStart?.(); setBrand(event.target.value); }}
        disabled={disabled || busy || active || needsStatusCheck} inputProps={{ maxLength: 200 }} helperText="Example: Bimetini" />
      <TextField sx={{ gridColumn: '1 / -1' }} label="C. Additional information" multiline minRows={3}
        value={additional} onChange={event => setAdditional(event.target.value)} disabled={disabled || busy || active || needsStatusCheck}
        inputProps={{ maxLength: 6000 }} placeholder="For example: Laos, TLPH Pharma, oncology, available on inquiry, 15 mg tablets, 60 tablets per bottle."
        helperText="Any order is fine. Include the company, origin, subcategory, availability, dosage form, strength and pack details you know. Details you do not provide stay empty." />
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'minmax(0,1fr) 160px',
      md: 'minmax(220px,440px) 160px max-content' }, gap: 2, alignItems: 'start', mt: 2 }}>
      <TranslationModelSelect models={models} label="AI model" disabled={disabled || busy || active || needsStatusCheck || !models.catalogue?.models.length}
        sx={{ width: '100%' }} />
      <TextField select fullWidth size="small" label="Usage mode" value={models.billingMode}
        disabled={disabled || busy || active || needsStatusCheck} onChange={event => models.setBillingMode(event.target.value)}>
        <MenuItem value="auto">Auto</MenuItem><MenuItem value="free">Free</MenuItem><MenuItem value="paid">Paid</MenuItem>
      </TextField>
      <Button variant="contained" size="large" sx={{ minHeight: 40, whiteSpace: 'nowrap', gridColumn: { sm: '1 / -1', md: 'auto' } }}
        disabled={disabled || busy || active || needsStatusCheck || run?.status === 'uncertain' || !models.ready || !generic.trim() || !brand.trim()}
        onClick={() => start(!!run && ['failed', 'completed'].includes(run.status))}>
        {busy ? 'Starting…' : run?.status === 'uncertain' ? 'Resolve unconfirmed usage first' : held ? 'Waiting for budget' : active ? 'Generating…' : run?.status === 'failed' ? 'Retry generation'
          : run?.status === 'completed' ? 'Generate again' : 'Generate & fill fields'}
      </Button>
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{modeDescription}</Typography>
    {selectedModel?.enabled && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: .5 }}>
      {selectedModel.tier === 'free'
        ? models.billingMode === 'auto' && selectedModel.fallbackAvailable
          ? 'Starts free · same-model paid fallback can use the dashboard budget'
          : models.billingMode === 'auto' && selectedModel.fallbackAvailable === false
            ? 'Free route · quota limited · paid fallback is not configured'
            : 'Free route · quota limited'
        : 'Paid route · token usage and estimated cost are recorded in AI translations'}
    </Typography>}
    {(models.error || (models.catalogue && !models.ready)) && <Alert severity="info" sx={{ mt: 1 }}>
      {models.error || 'No model is available in this usage mode. Check the configured provider accounts or choose another mode.'}
      <Button size="small" disabled={refreshingModels || models.loading} onClick={async () => {
        setRefreshingModels(true); try { await models.refresh(); } finally { setRefreshingModels(false); }
      }}>{refreshingModels ? 'Refreshing…' : 'Refresh models'}</Button>
    </Alert>}
    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
      OpenAI, Gemini or DeepSeek · no paid search tools · continues in the background. Unknown details stay blank. Nothing is published until you save.
    </Typography>
    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    {(needsStatusCheck || unconfirmed.current) && <Alert severity="warning" sx={{ mt: 2 }}>The previous submission may already have been accepted. Check its saved status before starting different work.<Button disabled={checkingStatus} onClick={checkSavedGeneration}>{checkingStatus ? 'Checking…' : 'Check saved generation'}</Button></Alert>}
    {run && <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Stack direction="row" justifyContent="space-between" gap={1} flexWrap="wrap">
        <Typography fontWeight={600}>{run.label}</Typography>
        <Typography variant="body2">{run.status === 'uncertain' ? 'Needs attention' : held ? `${run.progress}% · Waiting for budget` : run.status === 'failed' ? 'Failed'
          : `${run.progress}% · ${run.status === 'completed' ? 'Ready' : 'Generating'}`}</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={run.progress} color={run.status === 'failed' ? 'error'
        : run.status === 'uncertain' || held ? 'warning' : 'primary'} sx={{ my: 1 }} />
      <Typography variant="body2">{held ? run.stage : active ? 'Preparing your medicine fields. You can continue working or leave this page.'
        : run.status === 'completed' ? 'Generation complete. Your form is ready to review and edit.' : run.stage}</Typography>
      {held && <Button component={RouterLink} to={`${adminBase}/ai-translations?view=usage`} target="_blank" rel="noopener noreferrer" size="small" sx={{ my: .5 }}>Open budget</Button>}
      <Typography variant="caption" color="text.secondary">{run.model} · {run.freeOnly ? `Free route · $${run.cost.usd}`
        : run.status === 'uncertain' ? `Unconfirmed usage · budget hold $${run.reserved.usd}` : `${run.costEstimated ? 'Estimated usage cost' : 'Recorded cost'} $${run.cost.usd}`}</Typography>
    </Box>}
    {message && <Alert severity="success" sx={{ mt: 2 }}>
      {message.applied.length ? `Filled: ${message.applied.map(key => names[key] || key).join(', ')}. Review the fields below, then save your medicine.`
        : 'Your existing content was preserved. Open details if you want to apply selected generated fields.'}
      {(message.needsManual || []).map(value => <Typography key={value} variant="body2">{value}</Typography>)}
    </Alert>}
    {!matches && <Alert severity="warning" sx={{ mt: 2 }}>This generation belongs to {run.input.brand} ({run.input.generic}).
      It cannot fill a form with different generic or brand names.</Alert>}
    {run?.sourceId && !sourceId && <Alert severity="info" sx={{ mt: 2 }}>
      This draft belongs to a saved medicine. Review its details before using it for a new medicine.{' '}
      <RouterLink to={`${adminBase}/medicines/en/${run.sourceId}/edit`}>Open saved medicine</RouterLink>
    </Alert>}
    {run?.status === 'uncertain' && <Alert severity="warning" sx={{ mt: 2 }}>
      This request has unconfirmed usage. Retry is disabled until its saved charge is reconciled. Your current form content is preserved.
      <Button component={RouterLink} to={`${adminBase}/ai-translations?view=usage`} target="_blank" rel="noopener noreferrer">Open budget &amp; usage</Button>
    </Alert>}
    <Accordion expanded={detailsOpen} onChange={(_, expanded) => setDetailsOpen(expanded)} disableGutters elevation={0}
      sx={{ mt: 2, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="medicine-generation-details" id="medicine-generation-details-toggle">
        <Typography variant="body2" fontWeight={600}>Generation details &amp; previous drafts</Typography>
      </AccordionSummary>
      <AccordionDetails id="medicine-generation-details" sx={{ p: { xs: 1.5, sm: 2 } }}>
        {history.length > 0 && <TextField select fullWidth size="small" label="Previous generations"
          SelectProps={{ MenuProps: { PaperProps: { sx: { maxWidth: 'calc(100vw - 32px)' } } } }}
          value={history.some(row => row.id === id) ? id : ''} onChange={event => { setMessage(null); setId(event.target.value); }}>
          <MenuItem value="" disabled>Choose a saved draft</MenuItem>
          {history.map(row => <MenuItem key={row.id} value={row.id} sx={{ whiteSpace: 'normal' }}>{row.label} · {row.status === 'held' ? 'Waiting for budget' : row.status} · {new Date(row.createdAt).toLocaleString()}</MenuItem>)}
        </TextField>}
        <Button component={RouterLink} to={`${adminBase}/ai-translations`} sx={{ my: 1 }}>Full history &amp; usage</Button>
        {run?.draft && <>
          <Typography variant="body2" color="text.secondary">{run.draft.scopeNote}</Typography>
          {run.draft.missing.length > 0 && <Alert severity="info" sx={{ mt: 1 }}>
            Not filled: {run.draft.missing.map(key => names[key] || key.replace('section:', '')).join(', ')}.
            No placeholder text is inserted into your medicine.
          </Alert>}
          <FormControlLabel sx={{ mt: 1 }} control={<Checkbox checked={onlyEmpty} onChange={event => setOnlyEmpty(event.target.checked)} />}
            label="Fill empty fields only; preserve my current content" />
          {!onlyEmpty && <Alert severity="warning">Selected fields will replace their current values. Existing variations remain protected.</Alert>}
          {items.map(row => <Box key={row.key} sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, minWidth: 0 }}>
            <FormControlLabel control={<Checkbox checked={selected.includes(row.key)} disabled={disabled || (row.key === 'variants' && hasVariants)}
              onChange={event => setSelected(previous => event.target.checked ? [...previous, row.key] : previous.filter(key => key !== row.key))} />}
              label={row.label} />
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 220, overflow: 'auto' }}>{text(row.value)}</Typography>
            {row.key === 'variants' && hasVariants && <Typography variant="caption">Your manual variation edits are preserved. You can edit all variations directly in the form.</Typography>}
            {(run.draft.evidence[row.key] || []).map((fact, index) => <Box key={index} sx={{ mt: 1 }}>
              <Chip size="small" variant="outlined" label={fact.kind === 'source' ? 'Official medicine information'
                : fact.kind === 'owner' ? 'Your additional information' : 'Your medicine template'} />
              {fact.url && <Link display="block" href={fact.url} target="_blank" rel="noopener noreferrer" sx={{ overflowWrap: 'anywhere' }}>{fact.title}</Link>}
              {fact.quote && <Box component="details" sx={{ mt: .5 }}><Typography component="summary" variant="caption" sx={{ cursor: 'pointer' }}>View supporting text</Typography>
                <Typography variant="caption" display="block" sx={{ overflowWrap: 'anywhere' }}>{fact.quote}</Typography>
              </Box>}
            </Box>)}
          </Box>)}
          <Button sx={{ mt: 2 }} variant="outlined" disabled={disabled || !matches || !selected.length} onClick={apply}>Apply selected fields to form</Button>
        </>}
      </AccordionDetails>
    </Accordion>
  </Box>;
}
